<template>
    <section class="min-h-screen flex items-stretch text-white bg-[#F7F7F7]">
        <!-- Side Menu -->
        <div class="hidden md:flex flex-col justify-between bg-[#090446] text-white w-1/6 p-4">
            <div class="sticky top-0 bg-[#090446] p-4 z-20">
                <p class="text-lg font-semibold">
                    Total Progress: {{ progressDetail.totalAnswers1 }}/{{ progressDetail.totalQuestions1 }}
                </p>
                <div class="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                    <div class="bg-[#ab064e] h-2.5 rounded-full" :style="{ width: progressPercentage + '%' }"></div>
                </div>
            </div>
            <div class="mt-auto">
                <router-link to="/privacy-policy" class="text-white hover:text-[#ab064e]">
                    Privacy Policy
                </router-link>
            </div>
        </div>

        <!-- Main Content -->
        <div class="flex-1 flex flex-col items-center py-6 md:px-16 px-4 z-0">
            <!-- Instructions Card -->
            <div id="instructions-card" class="bg-white text-black p-6 rounded-lg shadow-lg mb-6 w-full lg:w-3/4">
                <div class="flex items-center justify-center mb-4">
                    <a href="#" class="flex items-center">
                        <img src="../assets/images/logo.png" class="h-24" alt="Logo" />
                    </a>
                </div>
                <p class="text-lg">You will see a series of statements like this:</p>
                <p class="text-lg mt-4"><b>Statement</b>: "Once I get going on a project, I like to finish."</p>
                <p class="text-lg mt-4">Choose the response that best reflects your typical experience, as honestly as possible.</p>
                <ul class="text-lg mt-4">
                    <li>1. Strongly Disagree</li>
                    <li>2. Somewhat Disagree</li>
                    <li>3. Neutral</li>
                    <li>4. Somewhat Agree</li>
                    <li>5. Strongly Agree</li>
                </ul>
            </div>

            <!-- Questions Card -->
            <div id="questions-card" class="bg-white text-black p-6 rounded-lg shadow-lg w-full lg:w-3/4">
                <div class="overflow-x-auto">
                    <table class="min-w-full border-collapse">
                        <thead>
                            <tr>
                                <th class="border-b p-4 text-left w-2/5">Question</th>
                                <th v-for="num in 5" :key="'header_' + num" class="border-b p-4 text-center w-1/12">
                                    {{ ['Strongly Disagree', 'Somewhat Disagree', 'Neutral', 'Somewhat Agree', 'Strongly Agree'][num - 1] }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(question, index) in questions" :key="question.question_id" class="border-t">
                                <td class="p-4 w-2/5">{{ index + 1 }}. {{ question.question }}</td>
                                <td v-for="num in 5" :key="num" class="p-4 text-center w-1/12">
                                    <label class="flex justify-center items-center cursor-pointer w-full h-full">
                                        <input type="radio" :name="'question_' + question.question_id" :value="num"
                                            @change="handleAnswerChange(question.question_id, num)"
                                            :checked="selectedAnswers[question.question_id] === num" class="hidden" />
                                        <span class="flex justify-center items-center w-12 h-12 border border-gray-300 rounded-md text-lg font-semibold"
                                            :class="{ 'bg-[#0A0446] text-white': selectedAnswers[question.question_id] === num, 'hover:bg-gray-200': selectedAnswers[question.question_id] !== num }">
                                            {{ num }}
                                        </span>
                                    </label>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="text-center mt-8">
                    <button class="relative inline-flex items-center justify-center px-16 py-3 bg-[#0A0446] text-white rounded-lg hover:bg-[#0A0446] transition-all duration-300 h-10"
                        @click="continueToNextPage">
                        <svg v-if="isLoading" class="animate-spin h-5 w-5 text-white absolute"
                            xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                        </svg>
                        <span v-else>Finish</span>
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import Api from '../router/api'

export default {
    name: 'AssessmentSurvey',
    data() {
        return {
            url: '',
            questions: [],
            selectedAnswers: {},
            progressDetail: { totalQuestions1: 0, totalAnswers1: 0 },
            isLoading: false
        };
    },
    mounted() {
        this.url = this.$route.fullPath.split('/')[2];
        this.getQuestionsAndProgress(this.url);
    },
    computed: {
        progressPercentage() {
            return (this.progressDetail.totalAnswers1 / this.progressDetail.totalQuestions1) * 100;
        }
    },
    methods: {
        async getQuestionsAndProgress(url) {
            try {
                const [questionsRes, progressRes] = await Promise.all([
                    Api.getAssessmentQuestions(url),
                    Api.getAssessmentProgress(url)
                ]);
                this.questions = questionsRes.data.res;
                this.selectedAnswers = this.questions.reduce((acc, q) => ({ ...acc, [q.question_id]: q.response }), {});
                this.progressDetail = progressRes.data.res;
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        },
        handleAnswerChange(questionId, answer) {
            this.selectedAnswers[questionId] = answer;

            // Send API request to save answer
            Api.saveQuestionResponse({ question_id: questionId, response: answer, token: this.url });

            // Recalculate progress based on unique answered questions
            this.progressDetail.totalAnswers1 = Object.values(this.selectedAnswers).filter(a => a !== null).length;
        },
        async continueToNextPage() {
            if (Object.keys(this.selectedAnswers).length < this.questions.length) {
                return this.$swal({ icon: "warning", title: "Warning", text: "Please answer all the questions." });
            }
            this.isLoading = true;
            try {
                const response = await Api.submitAssessment(this.url);
                if (response.data.status === "success") this.$router.push(`/assessment-complete/${this.url}`);
            } catch (error) {
                console.error("Submission failed:", error);
            }
            this.isLoading = false;
        }
    }
};
</script>



// WEBPACK FOOTER //
// src/components/AssessmentSurvey.vue